import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { OrderLimitSelfMeta } from '../../api/OrderLimitSelfMetas';
import * as OrderLimitSelfMetasApi from '../../api/OrderLimitSelfMetas';
import * as GoodsApi from '../../api/Goods';

const { Text, Paragraph } = Typography;

type GoodsOption = { value: string; label: string };

type SelfWarehouseRef = { id: number; name: string };

type Props = {
  open: boolean;
  selfWarehouse: SelfWarehouseRef | null;
  onClose: () => void;
  /** После правки default-пула (order_limit_self). */
  onSelfChanged?: (row: SelfWarehouseRef & { order_limit_self: number }) => void;
};

const parseArticleTokens = (raw: string) =>
  [
    ...new Set(
      raw
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];

const goodsLabel = (g: { article: string; name?: string | null }) =>
  g.name ? `${g.article} — ${g.name}` : g.article;

const ArticlePicker: React.FC<{
  value: string[];
  onChange: (next: string[]) => void;
  options: GoodsOption[];
  onOptionsChange: (next: GoodsOption[]) => void;
}> = ({ value, onChange, options, onOptionsChange }) => {
  const [searching, setSearching] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [resolving, setResolving] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );

  const mergeOptions = (extra: GoodsOption[]) => {
    onOptionsChange(
      [...extra, ...options].filter(
        (o, i, arr) => arr.findIndex((x) => x.value === o.value) === i,
      ),
    );
  };

  const searchGoods = (q: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q.trim()) return;
    searchTimer.current = setTimeout(async () => {
      const qTrim = q.trim();
      setSearching(true);
      try {
        const res = await GoodsApi.getAll({
          name: qTrim,
          article: qTrim,
          alias: qTrim,
          limit: 30,
          page: 1,
        });
        const list = Array.isArray(res) ? res : res.data;
        mergeOptions(
          list.map((g) => ({ value: g.article, label: goodsLabel(g) })),
        );
      } catch (e: any) {
        message.error(e?.message || String(e));
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const resolvePaste = async () => {
    const tokens = parseArticleTokens(pasteText);
    if (!tokens.length) {
      message.warning('Вставьте список артикулов');
      return;
    }
    setResolving(true);
    try {
      const found: GoodsOption[] = [];
      const missing: string[] = [];
      const chunk = 20;
      for (let i = 0; i < tokens.length; i += chunk) {
        const slice = tokens.slice(i, i + chunk);
        const results = await Promise.all(
          slice.map(async (article) => {
            try {
              const g = await GoodsApi.getOne(article);
              if (!g) return { ok: false as const, article };
              return {
                ok: true as const,
                option: { value: g.article, label: goodsLabel(g) },
              };
            } catch {
              return { ok: false as const, article };
            }
          }),
        );
        for (const r of results) {
          if (r.ok) found.push(r.option);
          else missing.push(r.article);
        }
      }
      if (found.length) {
        mergeOptions(found);
        onChange([...new Set([...value, ...found.map((f) => f.value)])]);
      }
      if (missing.length) {
        message.warning(
          `Не найдено в каталоге (${missing.length}): ${missing
            .slice(0, 8)
            .join(', ')}${missing.length > 8 ? '…' : ''}`,
        );
      } else if (found.length) {
        message.success(`Добавлено: ${found.length}`);
      }
      setPasteText('');
    } catch (e: any) {
      message.error(e?.message || String(e));
    } finally {
      setResolving(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      <Select
        mode="multiple"
        showSearch
        allowClear
        placeholder="Поиск артикула / названия"
        style={{ width: '100%' }}
        value={value}
        options={options}
        filterOption={false}
        loading={searching}
        onSearch={searchGoods}
        onChange={(next: string[]) => onChange(next)}
        optionFilterProp="label"
        maxTagCount="responsive"
      />
      <Input.TextArea
        rows={4}
        value={pasteText}
        onChange={(e) => setPasteText(e.target.value)}
        placeholder="Вставка списком: пробел, запятая или новая строка"
      />
      <Button onClick={() => void resolvePaste()} loading={resolving} block>
        Найти в каталоге и добавить
      </Button>
      <Text type="secondary" style={{ fontSize: 12 }}>
        В группу попадут только артикулы, найденные в API. На физскладе
        exclusive-групп может быть несколько; один артикул — только в одной.
      </Text>
    </Space>
  );
};

export const SelfMetaGroupsDrawer: React.FC<Props> = ({
  open,
  selfWarehouse,
  onClose,
  onSelfChanged,
}) => {
  const selfWarehouseId = selfWarehouse?.id ?? null;
  const [rows, setRows] = useState<OrderLimitSelfMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [orderLimit, setOrderLimit] = useState(0);
  const [createArticles, setCreateArticles] = useState<string[]>([]);
  const [createOptions, setCreateOptions] = useState<GoodsOption[]>([]);
  const [editArticlesId, setEditArticlesId] = useState<number | null>(null);
  const [editArticles, setEditArticles] = useState<string[]>([]);
  const [editOptions, setEditOptions] = useState<GoodsOption[]>([]);

  const load = useCallback(async () => {
    if (!selfWarehouseId) return;
    setLoading(true);
    try {
      setRows(await OrderLimitSelfMetasApi.list(selfWarehouseId));
    } catch (e: any) {
      message.error(e?.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selfWarehouseId]);

  useEffect(() => {
    if (open && selfWarehouseId) void load();
  }, [open, selfWarehouseId, load]);

  const syncSelfFromDefault = (meta: OrderLimitSelfMeta) => {
    if (!meta.is_default || !selfWarehouse) return;
    onSelfChanged?.({
      ...selfWarehouse,
      order_limit_self: meta.order_limit,
    });
  };

  const openCreate = () => {
    setName('');
    setOrderLimit(0);
    setCreateArticles([]);
    setCreateOptions([]);
    setCreateOpen(true);
  };

  const openEditArticles = (row: OrderLimitSelfMeta) => {
    setEditArticlesId(row.id);
    setEditArticles([...row.articles]);
    setEditOptions(row.articles.map((a) => ({ value: a, label: a })));
  };

  const createMeta = async () => {
    if (!selfWarehouseId) return;
    const n = name.trim();
    if (!n) {
      message.warning('Укажите название');
      return;
    }
    setSaving(true);
    try {
      await OrderLimitSelfMetasApi.create({
        self_warehouse_id: selfWarehouseId,
        name: n,
        order_limit: orderLimit,
        articles: createArticles,
      });
      message.success('Meta создана');
      setCreateOpen(false);
      setName('');
      setOrderLimit(0);
      setCreateArticles([]);
      setCreateOptions([]);
      await load();
    } catch (e: any) {
      message.error(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: OrderLimitSelfMeta, checked: boolean) => {
    try {
      const updated = await OrderLimitSelfMetasApi.update(row.id, {
        is_active: checked,
      });
      setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
      message.success(
        checked
          ? row.is_default
            ? 'Default включена'
            : 'Meta включена'
          : row.is_default
            ? 'Default выключена — остатки вне групп = 0'
            : 'Meta выключена — остатки 0',
      );
    } catch (e: any) {
      message.error(e?.message || String(e));
    }
  };

  const saveLimit = async (row: OrderLimitSelfMeta, limit: number) => {
    try {
      const updated = await OrderLimitSelfMetasApi.update(row.id, {
        order_limit: limit,
      });
      setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
      syncSelfFromDefault(updated);
    } catch (e: any) {
      message.error(e?.message || String(e));
    }
  };

  const replaceArticles = async () => {
    if (editArticlesId == null) return;
    const row = rows.find((r) => r.id === editArticlesId);
    if (!row) return;
    const next = editArticles;
    setSaving(true);
    try {
      const toRemove = row.articles.filter((a) => !next.includes(a));
      const toAdd = next.filter((a) => !row.articles.includes(a));
      let updated = row;
      if (toRemove.length) {
        updated = await OrderLimitSelfMetasApi.removeArticles(row.id, toRemove);
      }
      if (toAdd.length) {
        updated = await OrderLimitSelfMetasApi.addArticles(row.id, toAdd);
      }
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setEditArticlesId(null);
      message.success('Артикулы обновлены');
    } catch (e: any) {
      message.error(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={720}
        title={`Лимиты и группы${
          selfWarehouse?.name ? ` · ${selfWarehouse.name}` : ''
        }`}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
            disabled={!selfWarehouseId}
          >
            Создать группу
          </Button>
        }
      >
        <Paragraph type="secondary">
          <Text strong>Default</Text> — всё вне exclusive-групп; у каждой
          группы свой дневной лимит на физскладе. Soft-zero не отключает склад.
        </Paragraph>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          size="small"
          pagination={false}
          columns={[
            {
              title: 'Название',
              dataIndex: 'name',
              render: (v: string, row) => (
                <Space direction="vertical" size={0}>
                  <Space size={6}>
                    <Text strong>{v}</Text>
                    {row.is_default ? <Tag color="purple">default</Tag> : null}
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.is_default
                      ? 'артикулы не из exclusive-групп'
                      : `${row.article_count} арт.`}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'Лимит',
              dataIndex: 'order_limit',
              width: 110,
              render: (v: number, row) => (
                <InputNumber
                  min={0}
                  size="small"
                  value={v}
                  style={{ width: 90 }}
                  onChange={(n) => {
                    if (typeof n === 'number') void saveLimit(row, n);
                  }}
                />
              ),
            },
            {
              title: 'Вкл',
              dataIndex: 'is_active',
              width: 70,
              render: (v: boolean, row) => (
                <Switch
                  checked={v}
                  onChange={(checked) => void toggleActive(row, checked)}
                />
              ),
            },
            {
              title: '',
              key: 'actions',
              width: 160,
              render: (_: unknown, row: OrderLimitSelfMeta) =>
                row.is_default ? null : (
                  <Space size={4} wrap>
                    <Button size="small" onClick={() => openEditArticles(row)}>
                      Артикулы
                    </Button>
                    <Popconfirm
                      title="Удалить meta?"
                      onConfirm={async () => {
                        try {
                          await OrderLimitSelfMetasApi.remove(row.id);
                          message.success('Удалено');
                          await load();
                        } catch (e: any) {
                          message.error(e?.message || String(e));
                        }
                      }}
                    >
                      <Button size="small" danger>
                        Удалить
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
            },
          ]}
        />
      </Drawer>

      <Modal
        open={createOpen}
        title="Новая meta-группа"
        onCancel={() => setCreateOpen(false)}
        onOk={() => void createMeta()}
        confirmLoading={saving}
        okText="Создать"
        width={560}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="Название" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Form.Item>
          <Form.Item label="Дневной лимит" extra="0 = лимита нет">
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={orderLimit}
              onChange={(v) => setOrderLimit(typeof v === 'number' ? v : 0)}
            />
          </Form.Item>
          <Form.Item label="Артикулы">
            <ArticlePicker
              value={createArticles}
              onChange={setCreateArticles}
              options={createOptions}
              onOptionsChange={setCreateOptions}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editArticlesId != null}
        title="Артикулы meta"
        onCancel={() => setEditArticlesId(null)}
        onOk={() => void replaceArticles()}
        confirmLoading={saving}
        okText="Сохранить"
        width={560}
        destroyOnClose
      >
        <ArticlePicker
          value={editArticles}
          onChange={setEditArticles}
          options={editOptions}
          onOptionsChange={setEditOptions}
        />
        <div style={{ marginTop: 8 }}>
          <Tag>exclusive: артикул только в одной meta на физскладе</Tag>
        </div>
      </Modal>
    </>
  );
};
